import _ from 'lodash';
import classNames from 'classnames';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import type { EmojiCategory as EmojiCategoryType } from '../utils/emojiData';
import styles from './EmojiPicker.module.scss';
import lightbulbIcon from '../icons/non-prefilled/sidebar/lightbulb.svg';
import flagIcon from '../icons/non-prefilled/sidebar/flag.svg';
import homeReactionIcon from '../icons/non-prefilled/emojis/homeReaction.svg';
import smileysPeopleIcon from '../icons/non-prefilled/emojis/smileysPeople.svg';
import activitiesIcon from '../icons/non-prefilled/emojis/activities.svg';
import animalsNatureIcon from '../icons/non-prefilled/emojis/animalsNature.svg';
import foodDrinksIcon from '../icons/non-prefilled/emojis/foodDrinks.svg';
import travelPlacesIcon from '../icons/non-prefilled/emojis/travelPlaces.svg';
import SVGIcon from './SVGIcon';
import ScrollArea from './ScrollArea';
import type { Emoji } from '../utils/emoji';
import {
  applySkinToneToEmoji,
  emojiList,
  getEmojiItemByName,
  searchEmoji,
} from '../utils/emoji';
import useAppSelector from '../app/redux/useAppSelector';
import addTestId from '../utils/addTestId';
import EmojiComponent from '../post/comment/Emoji';
import useEventListener from '../utils/useEventListener';

const EMOJI_COUNT_PER_ROW = parseInt(styles.emojiCountPerRow, 10);
const EMOJI_INDEX_ATTRIBUTE = 'data-emoji-index';

interface EmojiCategoryProps {
  title: string;
  startIndex: number;
  activeItemIndex: number;
  items: { emoji: { name: string; char: string }; tonedChar: string }[];
  onPick: (emoji: string) => void;
  onItemMouseEnter: (index: number) => void;
}

const EmojiCategory = forwardRef<HTMLDivElement, EmojiCategoryProps>(
  (
    { title, startIndex, activeItemIndex, items, onPick, onItemMouseEnter },
    ref,
  ) => (
    <div ref={ref} className={styles.category}>
      <h3 className={styles.title}>{title}</h3>
      <div className={styles.emojiList}>
        {items.map(({ emoji, tonedChar }, itemIndex) => {
          const index = itemIndex + startIndex;
          return (
            <button
              key={tonedChar}
              type="button"
              title={emoji.name}
              {...addTestId(`emoji_${emoji.name}`)}
              className={classNames(styles.emoji, {
                [styles.isActive]: index === activeItemIndex,
              })}
              onClick={() => onPick(tonedChar)}
              onMouseEnter={() => onItemMouseEnter(index)}
              {...{ [EMOJI_INDEX_ATTRIBUTE]: index }}
            >
              <EmojiComponent char={tonedChar} size="regular" />
            </button>
          );
        })}
      </div>
    </div>
  ),
);

interface CategoryTabButtonProps {
  icon: string;
  selectedCategoryId: CATEGORY;
  categoryId: CATEGORY;
  onClick: (categoryId: CATEGORY) => void;
  'aria-label': string;
}

const CategoryTabButton = ({
  icon,
  selectedCategoryId,
  categoryId,
  onClick,
  'aria-label': ariaLabel,
}: CategoryTabButtonProps) => (
  <button
    type="button"
    role="tab"
    aria-label={ariaLabel}
    aria-selected={selectedCategoryId === categoryId}
    onClick={() => onClick(categoryId)}
    className={styles.tabButton}
  >
    <SVGIcon>{icon}</SVGIcon>
  </button>
);

enum CATEGORY {
  Home,
  SmileysPeople,
  AnimalsNature,
  FoodDrinks,
  Activities,
  TravelPlaces,
  ObjectSymbols,
  Flags,
}

const CATEGORY_MAP: Record<EmojiCategoryType, CATEGORY> = {
  people: CATEGORY.SmileysPeople,
  animals_and_nature: CATEGORY.AnimalsNature,
  food_and_drink: CATEGORY.FoodDrinks,
  travel_and_places: CATEGORY.TravelPlaces,
  activity: CATEGORY.Activities,
  objects: CATEGORY.ObjectSymbols,
  symbols: CATEGORY.ObjectSymbols,
  flags: CATEGORY.Flags,
};

const EMOJI_CATEGORIES: Map<
  CATEGORY,
  { title: string; icon: string; emojis: Emoji[] }
> = new Map([
  [
    CATEGORY.SmileysPeople,
    { title: 'Smileys & People', icon: smileysPeopleIcon, emojis: [] },
  ],
  [
    CATEGORY.AnimalsNature,
    { title: 'Animals & Nature', icon: animalsNatureIcon, emojis: [] },
  ],
  [
    CATEGORY.FoodDrinks,
    { title: 'Food & Drink', icon: foodDrinksIcon, emojis: [] },
  ],
  [
    CATEGORY.Activities,
    { title: 'Activities', icon: activitiesIcon, emojis: [] },
  ],
  [
    CATEGORY.TravelPlaces,
    { title: 'Travel & Places', icon: travelPlacesIcon, emojis: [] },
  ],
  [
    CATEGORY.ObjectSymbols,
    { title: 'Objects & Symbols', icon: lightbulbIcon, emojis: [] },
  ],
  [CATEGORY.Flags, { title: 'Flags', icon: flagIcon, emojis: [] }],
]);
emojiList.forEach((emoji) => {
  EMOJI_CATEGORIES.get(CATEGORY_MAP[emoji.category])?.emojis.push(emoji);
});

const toneEmojis = (emojis: Emoji[], modifier: string | null) =>
  emojis.map((emoji) => ({
    emoji,
    tonedChar: applySkinToneToEmoji(emoji, modifier),
  }));

const useIsMouseMoving = () => {
  const isMouseMovingRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleMouseMove = () => {
    isMouseMovingRef.current = true;

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      isMouseMovingRef.current = false;
      timerRef.current = null;
    }, 500);
  };

  return { isMouseMovingRef, onMouseMove: handleMouseMove };
};

interface EmojiSection {
  id: CATEGORY;
  title: string;
  items: { emoji: Emoji; tonedChar: string }[];
}

interface NavigateOptions {
  sections: EmojiSection[];
  currentIndex: number;
  direction: 'up' | 'down' | 'left' | 'right';
}

const navigate = ({ sections, currentIndex, direction }: NavigateOptions) => {
  switch (direction) {
    case 'left':
      return Math.max(currentIndex - 1, 0);
    case 'right':
      return Math.min(
        currentIndex + 1,
        sections.reduce((acc, s) => acc + s.items.length, 0) - 1,
      );
    default: {
      let currentSectionIndex = 0;
      let currentIndexInSection = currentIndex;
      sections.some((section) => {
        if (currentIndexInSection < section.items.length) return true;

        currentIndexInSection -= section.items.length;
        currentSectionIndex += 1;
        return false;
      });

      const currentSection = sections[currentSectionIndex];
      if (!currentSection) return null;

      const column = currentIndexInSection % EMOJI_COUNT_PER_ROW;

      if (direction === 'down') {
        const remainingInCurrentSection =
          currentSection.items.length - currentIndexInSection - 1;

        if (EMOJI_COUNT_PER_ROW < remainingInCurrentSection) {
          return currentIndex + EMOJI_COUNT_PER_ROW;
        }
        if (column + remainingInCurrentSection > EMOJI_COUNT_PER_ROW) {
          return currentIndex + remainingInCurrentSection;
        }
        const nextSection = sections[currentSectionIndex + 1];
        if (!nextSection) return currentIndex + remainingInCurrentSection;

        return (
          currentIndex +
          remainingInCurrentSection +
          Math.min(column + 1, nextSection.items.length)
        );
      }

      if (currentIndexInSection >= EMOJI_COUNT_PER_ROW) {
        return currentIndex - EMOJI_COUNT_PER_ROW;
      }
      const prevSection = sections[currentSectionIndex - 1];
      if (!prevSection) return 0;

      const countInTrailingLine =
        prevSection.items.length % EMOJI_COUNT_PER_ROW || EMOJI_COUNT_PER_ROW;
      if (countInTrailingLine > column) {
        return (
          currentIndex - currentIndexInSection - (countInTrailingLine - column)
        );
      }

      return currentIndex - currentIndexInSection - 1;
    }
  }
};

interface EmojiPickerProps {
  query?: string;
  onClearQuery?: () => void;
  inputContainer?: HTMLDivElement | null;
  onPick: (emoji: string) => void;
  onEscapePress?: () => void;
}

const EmojiPicker = ({
  query,
  onClearQuery,
  inputContainer,
  onPick,
  onEscapePress,
}: EmojiPickerProps) => {
  const [index, setIndex] = useState(0);
  const [previousQuery, setPreviousQuery] = useState(query);
  const [selectedCategoryId, setSelectedCategoryId] = useState<CATEGORY>(
    CATEGORY.Home,
  );
  const selectedCategoryIdRef = useRef(selectedCategoryId);
  useEffect(() => {
    selectedCategoryIdRef.current = selectedCategoryId;
  });

  if (query !== previousQuery) {
    setPreviousQuery(query);
    if (query && !previousQuery) {
      setSelectedCategoryId(CATEGORY.Home);
    }
    setIndex(0);
  }

  const scrollAreaRef = useRef<OverlayScrollbarsComponentRef | null>(null);
  const categoryNodesRef = useRef<Map<CATEGORY, HTMLDivElement | null>>(
    new Map(),
  );

  const scrollToCategory = (categoryId: CATEGORY) => {
    const node = categoryNodesRef.current.get(categoryId);
    if (node) {
      const viewport = scrollAreaRef.current?.osInstance()?.elements().viewport;
      if (viewport) {
        viewport.scrollTop = node.offsetTop;
      }
    }
  };

  const onTabButtonClick = (categoryId: CATEGORY) => {
    setSelectedCategoryId(categoryId);
    scrollToCategory(categoryId);

    if (categoryId !== CATEGORY.Home) onClearQuery?.();
  };

  useEffect(() => {
    scrollToCategory(query ? CATEGORY.Home : selectedCategoryIdRef.current);
  }, [query]);

  const modifier = useAppSelector((state) => state.ui.emojiSkinToneModifier);
  const searchResultSection = useMemo<EmojiSection | null>(() => {
    if (!query) return null;
    const items = searchEmoji(query).map((result) => result.item);
    return {
      id: CATEGORY.Home,
      title: items.length ? 'Results' : 'No results',
      items: toneEmojis(items, modifier),
    };
  }, [query, modifier]);

  const defaultSections = useMemo(
    () =>
      [...EMOJI_CATEGORIES.entries()].map(([id, { title, emojis }]) => ({
        id,
        title,
        items: toneEmojis(emojis, modifier),
      })),
    [modifier],
  );

  const recentlyUsedEmojis = useAppSelector(
    (state) => state.ui.recentlyUsedEmojis,
  ).slice(0, 2 * EMOJI_COUNT_PER_ROW);

  const sections: EmojiSection[] = searchResultSection
    ? [searchResultSection]
    : [
        {
          id: CATEGORY.Home,
          title: 'Recent',
          items: toneEmojis(
            _.compact(_.uniq(recentlyUsedEmojis).map(getEmojiItemByName)),
            modifier,
          ),
        },
        ...defaultSections,
      ];

  const startIndexes = sections.reduce(
    (indexes, current) => [
      ...indexes,
      (_.last(indexes) ?? 0) + current.items.length,
    ],
    [0],
  );

  const keyboardHandler = (event: KeyboardEvent | ReactKeyboardEvent) => {
    const map: Record<typeof event['key'], NavigateOptions['direction']> = {
      ArrowLeft: 'left',
      ArrowRight: 'right',
      ArrowDown: 'down',
      ArrowUp: 'up',
    };

    const direction = map[event.key];
    if (direction) {
      const nextIndex = navigate({ sections, direction, currentIndex: index });
      if (typeof nextIndex === 'number') {
        setIndex(nextIndex);
        event.preventDefault();

        const node = containerRef.current?.querySelector(
          `[${EMOJI_INDEX_ATTRIBUTE}="${nextIndex}"]`,
        );
        node?.scrollIntoView({ block: 'nearest' });
      }

      return;
    }

    if (event.key === 'Enter') {
      const item = sections.map((s) => s.items).flat()[index];
      if (item) {
        onPick(item.tonedChar);
        event.preventDefault();
      }
    }

    if (event.key === 'Escape') {
      if (onEscapePress) {
        onEscapePress();
        event.preventDefault();
      }
    }
  };

  useEventListener(inputContainer, 'keydown', keyboardHandler, {
    options: { capture: true },
  });

  const [hasScrolled, setHasScrolled] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const { isMouseMovingRef, onMouseMove } = useIsMouseMoving();

  return (
    <div
      ref={containerRef}
      className={styles.container}
      aria-label="Emoji picker"
      onKeyDown={keyboardHandler}
    >
      <div
        className={classNames(styles.tabList, {
          [styles.hasScrolled]: hasScrolled,
        })}
        role="tablist"
        aria-label="Categories"
      >
        <CategoryTabButton
          icon={homeReactionIcon}
          categoryId={CATEGORY.Home}
          selectedCategoryId={selectedCategoryId}
          aria-label="Home"
          onClick={onTabButtonClick}
        />
        {[...EMOJI_CATEGORIES.entries()].map(([id, { title, icon }]) => (
          <CategoryTabButton
            key={id}
            icon={icon}
            categoryId={id}
            selectedCategoryId={selectedCategoryId}
            aria-label={title}
            onClick={onTabButtonClick}
          />
        ))}
      </div>
      <div
        role="tabpanel"
        className={styles.tabPanel}
        onMouseMove={onMouseMove}
      >
        <ScrollArea
          ref={scrollAreaRef}
          className={styles.scrollArea}
          events={{
            scroll: (event) => {
              const { viewport } = event.elements();
              sections
                .map((s) => s.id)
                .toReversed()
                .some((id) => {
                  const node = categoryNodesRef.current.get(id);
                  if (!node) return false;
                  const top = node.offsetTop - viewport.scrollTop;
                  if (top <= 0) {
                    setSelectedCategoryId(id);
                    return true;
                  }
                  return false;
                });
              setHasScrolled(viewport.scrollTop > 0);
            },
          }}
        >
          {sections.map((section, sectionIndex) => (
            <EmojiCategory
              ref={(node) => {
                categoryNodesRef.current.set(section.id, node);
              }}
              startIndex={startIndexes[sectionIndex] ?? 0}
              activeItemIndex={index}
              key={section.id}
              title={section.title}
              items={section.items}
              onPick={onPick}
              onItemMouseEnter={(i) => {
                // Only update the index if the mouse is moving.
                // Otherwise, the callback is likely triggered by an item
                // moving under the cursor due to the keyboard navigation.
                if (isMouseMovingRef.current) {
                  setIndex(i);
                }
              }}
            />
          ))}
        </ScrollArea>
      </div>
    </div>
  );
};

export default EmojiPicker;
